export const PRIVACY = `
# Privacy Policy
*Last Updated: February 27, 2026*

Exportpadi is a high-volume commodity export marketplace domiciled in Nigeria. We connect verified local farmers and aggregators with international buyers for bulk agricultural commodity transactions.

We are committed to protecting your personal data and ensuring transparency in how it is collected and used.

---

## 1. Information We Collect

### A. Information You Provide

We collect information directly from you when you create an account, complete KYC/KYB verification, submit product listings, execute trade contracts, or communicate with our team. This may include:

- Full name and company name
- Business registration details
- Identification documents (as required for compliance)
- Contact information (email, phone number, address)
- Bank account or payment settlement details
- Shipping and export documentation

### B. Automatically Collected Data

When you use our platform, we may collect:

- Device and browser information
- IP address and access logs
- Usage behavior on the platform
- Cookies and tracking technologies

---

## 2. How We Use Your Information

We process personal and business data to:

- Facilitate high-volume commodity transactions
- Conduct KYC/KYB and regulatory compliance checks
- Draft and manage export contracts
- Coordinate logistics and shipping documentation
- Process payments and escrow settlements
- Improve platform functionality
- Communicate service updates and trade notifications

Exportpadi does **not sell personal data** to third parties.

---

## 3. Sharing of Information

We share information strictly where necessary with:

- Licensed payment processors and banking institutions
- Inspection agencies and commodity quality assessors
- Freight forwarders and shipping lines
- Customs brokers and port authorities
- Regulatory bodies where legally required
- Cloud hosting and technical infrastructure providers

All service providers are contractually obligated to maintain confidentiality and data protection standards.

---

## 4. Data Retention

We retain data for the duration of your account activity, as required for trade documentation and export compliance, and in accordance with Nigerian laws and international trade regulations.

You may request deletion of your account, subject to legal and regulatory retention requirements.

---

## 5. Your Rights

Depending on your jurisdiction, you may have rights to:

- Access your data
- Correct inaccurate information
- Request deletion (subject to legal requirements)
- Restrict or object to certain processing
- Request portability of your data

Requests may be submitted to: **privacy@exportpadi.com**

---

## 6. Data Security

Exportpadi implements SSL encryption, secure cloud infrastructure, role-based access controls, two-factor authentication, and regular security reviews.

While we apply industry best practices, no digital transmission is entirely secure.
`;

export const SHIPPING = `
# Shipping & Logistics Policy

Exportpadi facilitates bulk agricultural commodity exports primarily via international sea freight from Nigerian ports.

---

## 1. Export Shipping Model

All shipments are conducted under formal trade contracts and governed by agreed Incoterms between buyers and sellers. Shipping methods typically include:

- Full Container Load (FCL)
- Less than Container Load (LCL)
- Bulk vessel shipments (where applicable)

Primary export ports may include major Nigerian seaports such as Lagos ports and other designated export terminals.

---

## 2. Standard International Sea Freight Timelines

Estimated sea freight transit times after vessel departure:

- West Africa to Europe: approximately 2–4 weeks
- West Africa to North America: approximately 4–6 weeks
- West Africa to Asia/Middle East: approximately 4–7 weeks

These timelines may vary depending on port congestion, customs clearance, weather conditions, transshipment schedules, or regulatory inspections.

Exportpadi does not guarantee transit times, as ocean freight schedules are subject to global shipping conditions.

---

## 3. Processing & Documentation Timeline

Prior to vessel departure, the following processes apply:

- Commodity aggregation and bagging
- Quality inspection and certification
- Phytosanitary certification
- Export documentation processing
- Container stuffing and port delivery
- Customs clearance

The timeline depends on commodity type, volume, and regulatory requirements.

---

## 4. Customs & Import Responsibilities

International buyers are responsible for import permits, destination country customs duties, port handling charges, and local clearance requirements.

Exportpadi facilitates export compliance from Nigeria but does not control import regulations in destination countries.

---

## 5. Shipment Tracking

For containerized shipments, tracking is available via Bill of Lading number, container number, or the shipping line tracking portal. Buyers receive relevant documentation once cargo is confirmed on board.

---

## 6. Risk Transfer

Risk transfer is governed strictly by the agreed Incoterms stated in the contract (e.g., FOB, CIF, CFR). Buyers and sellers are encouraged to obtain marine cargo insurance where applicable.
`;

export const RETURN = `
# Returns, Claims & Dispute Policy

Due to the nature of bulk agricultural commodity exports, traditional retail returns do not apply.

---

## 1. Quality Assurance

Before shipment, independent inspections may be conducted, commodity grading standards are documented, and pre-shipment certificates may be issued. Specifications are defined in the signed sales contract.

---

## 2. Claims Procedure

If a buyer identifies discrepancies:

- Written notice must be submitted within the contractual notification period
- Supporting inspection evidence must be provided
- An independent inspection at destination may be required

Disputes are resolved in accordance with the signed trade agreement, agreed arbitration clauses, and applicable Nigerian commercial law or designated arbitration body.

---

## 3. Non-Returnable Nature

Bulk agricultural exports are not eligible for physical return due to international logistics constraints, commodity handling risks, and cross-border regulatory restrictions.

Resolutions are handled through contractual remedies, not product returns.
`;

export const COOKIE = `
# Cookie Policy
*Last Updated: February 27, 2026*

This policy explains how Exportpadi uses cookies and tracking technologies.

---

## 1. Types of Cookies We Use

### Strictly Necessary
Essential for platform operation, login authentication, trade dashboards, and security.

### Analytics
Help us understand how users interact with listings, dashboards, and transaction flows.

### Functional
Remember user preferences such as language, account type, and trade settings.

### Security & Fraud Prevention
Used to detect suspicious login attempts and prevent unauthorized transactions.

---

## 2. Third-Party Services

We may use analytics providers, cloud infrastructure providers, and payment processing services. These providers may set their own cookies in accordance with their respective privacy policies.

---

## 3. Managing Cookies

Users may manage cookies through browser settings or the platform cookie consent banner. Disabling essential cookies may affect core trading functionality.

---

## 4. Policy Updates

Exportpadi reserves the right to update these policies in line with regulatory changes, trade law updates, or platform enhancements. Continued use of the platform constitutes acceptance of any updates.
`;
